import React, { useState } from "react";
import firebase from "firebase/compat/app";
import DataBase from "../../dataBase/liquidGuides";
import { planWikiFixes, applyWikiFixes } from "../../Common/wikiSourceFix";

// Admin Dashboard → Guides: applies the reviewed Wikipedia fixes to Featured
// places (wrong article, photo, notes or pin). Load the fixes file, Check
// shows what would change (nothing is written), Apply writes the guides the
// signed-in account owns. See Common/wikiSourceFix.js. 2026-10-27.
const ACTION = { article: "new article", clear: "wrong article removed", pin: "pin moved" };

export default function FeaturedWikiFixPanel() {
  const [fixes, setFixes] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null); // planWikiFixes rows
  const [message, setMessage] = useState("");
  const [confirming, setConfirming] = useState(null); // { mine, byGuide, others } waiting for "Yes, fix"

  const load = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setRows(null);
    setMessage("");
    file.text().then((text) => {
      try {
        const list = JSON.parse(text).fixes;
        if (!Array.isArray(list)) throw new Error("no fixes");
        setFixes(list);
        setFileName(file.name);
        setMessage(`${list.length} fixes loaded.`);
      } catch (err) {
        setFixes(null);
        setMessage("That file isn't a Wikipedia fixes file.");
      }
    });
  };

  const check = () => {
    setBusy(true);
    setMessage("");
    setRows(null);
    DataBase.getFeaturedGuidesData((res) => {
      setBusy(false);
      if (!res.pass) {
        setMessage("Could not read the Featured guides.");
        return;
      }
      const plan = planWikiFixes(res.guides, fixes);
      setRows(plan);
      const count = (s) => plan.filter((r) => r.status === s).length;
      setMessage(
        `${count("ready")} to fix, ${count("done")} already fixed, ${count("changed")} edited since the review (left alone), ${count("missing")} not found.`
      );
    });
  };

  // Asks on the page, not with window.confirm: embedded browsers (e.g. the
  // VS Code preview) block confirm() silently, so Apply seemed to do nothing.
  const apply = () => {
    const uid = firebase.auth().currentUser?.uid;
    const ready = rows.filter((r) => r.status === "ready");
    const mine = ready.filter((r) => r.guide.owner === uid);
    if (mine.length === 0) {
      setMessage("None of these guides belong to the signed-in account, so they can't be changed from here.");
      return;
    }
    const byGuide = new Map();
    mine.forEach((r) => byGuide.set(r.guide.guide_id, [...(byGuide.get(r.guide.guide_id) || []), r]));
    setMessage("");
    setConfirming({ mine, byGuide, others: ready.length - mine.length });
  };

  const applyConfirmed = async () => {
    const { byGuide, others } = confirming;
    setConfirming(null);
    setBusy(true);
    let done = 0;
    let failed = 0;
    for (const [guideId, list] of byGuide) {
      let res;
      try {
        const data = applyWikiFixes(list[0].guide.data, list.map((r) => r.fix));
        // eslint-disable-next-line no-await-in-loop
        res = await new Promise((resolve) => DataBase.replaceGuideDataContent(guideId, data, resolve));
      } catch (err) {
        console.error("Wikipedia fix failed", guideId, err);
        res = { pass: false };
      }
      list.forEach((r) => (r.status = res && res.pass ? "fixed" : "failed"));
      if (res && res.pass) done += list.length;
      else failed += list.length;
      setRows([...rows]);
    }
    setBusy(false);
    setMessage(`${done} places fixed${failed ? `, ${failed} failed` : ""}${others ? `, ${others} in guides owned by another account (skipped)` : ""}.`);
  };

  const ready = rows ? rows.filter((r) => r.status === "ready").length : 0;
  return (
    <div className="admindash-lookup">
      <p className="tai-paste-title admindash-lookup-title">Featured guides: Wikipedia fixes</p>
      <p className="tai-paste-hint">
        Places whose photo and notes came from the wrong Wikipedia article. Load the reviewed fixes file
        (wiki-source-fixes-….json), Check, then Apply. A place edited since the review is left alone.
      </p>
      <div className="admindash-lookup-row">
        {/* file inputs are hidden app-wide (Modal.css); the label is the button */}
        <label className="tai-cancel-btn" style={{ margin: 0, cursor: "pointer" }}>
          Choose file…
          <input type="file" accept=".json,application/json" onChange={load} disabled={busy} aria-label="Fixes file" />
        </label>
        <button className="tai-cancel-btn" onClick={check} disabled={busy || !fixes}>
          {busy && !rows ? "Checking…" : "Check"}
        </button>
        {ready > 0 && !confirming && (
          <button className="tai-primary-btn" onClick={apply} disabled={busy}>
            {busy ? "Applying…" : `Apply to ${ready}`}
          </button>
        )}
        {confirming && (
          <>
            <button className="tai-primary-btn" onClick={applyConfirmed}>
              Yes, fix {confirming.mine.length} places in {confirming.byGuide.size} guides
            </button>
            <button className="tai-cancel-btn" onClick={() => setConfirming(null)}>
              Cancel
            </button>
          </>
        )}
      </div>
      {fileName !== "" && <p className="tai-paste-hint">{fileName}</p>}
      {message !== "" && <p className="tai-paste-hint">{message}</p>}
      {rows && rows.length > 0 && (
        <div style={{ maxHeight: 260, overflowY: "auto", fontSize: 13 }}>
          {rows.map((r) => (
            <div key={`${r.fix.guide_info_id}|${r.fix.place_id}`}>
              {r.fix.guide}: {r.fix.place} — {ACTION[r.fix.action] || r.fix.action}
              {r.fix.article ? ` (${r.fix.article})` : ""}
              {r.fix.set?.Name ? `, renamed “${r.fix.set.Name}”` : ""} — {r.status}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
